import { CommissionItem } from '@shared/commons/cache/Commissions/CommissionItem';
import { CommissionPlan } from '@shared/commons/cache/Commissions/CommissionPlan';
import { Instrument } from '@shared/commons/cache/Instrument';
import { Level1Calculator } from '@shared/commons/cache/Level1Calculator';
import { OrderTypeBaseParameters } from '@shared/commons/cache/OrderParams/order-type/OrderTypeBaseParameters';
import { RiskPlan } from '@shared/commons/cache/RiskPlan';
import { DataCache } from '@shared/commons/DataCache';
import { Resources } from '@shared/localizations/Resources';
import { SessionOperations } from '../Enums/Constants';
import { MathUtils } from '../MathUtils';
import { NumericUtils } from '../NumericUtils';
import { CouponCycle } from './CouponCycle';
import { DaysPerType, GeneralSymbolInfoFeatureEnum, InstrumentFeatureEnum, TradingInfoSymbolInfoFeatureEnum } from './InstrumentFeatureEnum';
import { InstrumentTradingBalance } from './InstrumentTradingBalance';
import { InstrumentTypes } from './InstrumentTypes';
import { InstrumentUtils } from './InstrumentUtils';
import { MarginTypes } from './MarginTypes';
import { PriceLimitMeasure } from './PriceLimitMeasure';
import { ProductType } from './ProductType';
import { QuotingType } from './QuotingType';
import { SwapType } from './SwapType';
import { TradingMode } from './TradingMode';
import { CurrencyUtils } from '../Asset/CurrencyUtils';
import { commisionSplitter } from '../Commission/CommisionSplitter';
import { CommissionOperationType } from '../Commission/CommissionEnums';
import { Enum } from '../Enum';
import { DayPeriods } from '../Session/DayPeriods';
import { SessionUtils } from '../Session/SessionUtils';
import { StringUtils } from '../StringUtils';
import { DateTimeUtils } from '../Time/DateTimeUtils';
import { OrderUtils } from '../Trading/OrderUtils';
class _InstrumentFeatureHelper {
    constructor() {
        this.FormatMargin_5ARG = function (init, warn, maint, sign, isShowAllKoef, precisions) {
            precisions = precisions || [];
            const initFormat = MathUtils.TruncateDouble(init, precisions[0] || 2);
            const warnFormat = MathUtils.TruncateDouble(warn, precisions[1] || 2);
            const maintFormat = MathUtils.TruncateDouble(maint, precisions[2] || 2);
            if (isShowAllKoef) {
                if (sign) {
                    return initFormat + ' ' + sign + ' / ' + warnFormat + ' ' + sign + ' / ' + maintFormat + ' ' + sign;
                }
                else {
                    return initFormat + '/' + warnFormat + '/' + maintFormat;
                }
            }
            return sign ? initFormat + ' ' + sign : initFormat.toString();
        };
    }
    getGeneralInfoItems() {
        const items = [];
        items.push(GeneralSymbolInfoFeatureEnum.SymbolName);
        items.push(GeneralSymbolInfoFeatureEnum.SymbolDescription);
        items.push(GeneralSymbolInfoFeatureEnum.TradingExchage);
        items.push(GeneralSymbolInfoFeatureEnum.MarketDataExchange);
        items.push(GeneralSymbolInfoFeatureEnum.SymbolType);
        items.push(GeneralSymbolInfoFeatureEnum.Underlier);
        items.push(GeneralSymbolInfoFeatureEnum.DeliveryMethod);
        items.push(GeneralSymbolInfoFeatureEnum.ContactMonth);
        items.push(GeneralSymbolInfoFeatureEnum.FirstTradeDate);
        items.push(GeneralSymbolInfoFeatureEnum.NoticeDate);
        items.push(GeneralSymbolInfoFeatureEnum.SettlementDate);
        items.push(GeneralSymbolInfoFeatureEnum.TradingBalance);
        items.push(GeneralSymbolInfoFeatureEnum.StrikePrice);
        items.push(GeneralSymbolInfoFeatureEnum.MaturityDate);
        items.push(GeneralSymbolInfoFeatureEnum.FaceValue);
        items.push(GeneralSymbolInfoFeatureEnum.CouponRate);
        items.push(GeneralSymbolInfoFeatureEnum.CouponCycle);
        items.push(GeneralSymbolInfoFeatureEnum.Ytm);
        items.push(GeneralSymbolInfoFeatureEnum.AccruedInterest);
        items.push(GeneralSymbolInfoFeatureEnum.PrevPaymentDate);
        items.push(GeneralSymbolInfoFeatureEnum.NextPaymentDate);
        items.push(GeneralSymbolInfoFeatureEnum.DaysPerMonth);
        items.push(GeneralSymbolInfoFeatureEnum.DaysPerYear);
        items.push(GeneralSymbolInfoFeatureEnum.Yield);
        items.push(GeneralSymbolInfoFeatureEnum.Asset);
        items.push(GeneralSymbolInfoFeatureEnum.ExerciseStyle);
        items.push(GeneralSymbolInfoFeatureEnum.Isin);
        items.push(GeneralSymbolInfoFeatureEnum.Industry);
        items.push(GeneralSymbolInfoFeatureEnum.Sector);
        items.push(GeneralSymbolInfoFeatureEnum.ExtSmFlag);
        return items;
    }
    getTradingInfoItems() {
        const items = [];
        items.push(TradingInfoSymbolInfoFeatureEnum.TradingStatus);
        items.push(TradingInfoSymbolInfoFeatureEnum.AllowedOperations);
        items.push(TradingInfoSymbolInfoFeatureEnum.AllowedOrderTypes);
        items.push(TradingInfoSymbolInfoFeatureEnum.ProductType);
        items.push(TradingInfoSymbolInfoFeatureEnum.DeliveryStatus);
        items.push(TradingInfoSymbolInfoFeatureEnum.CurrentSession);
        items.push(TradingInfoSymbolInfoFeatureEnum.SessionStatus);
        items.push(TradingInfoSymbolInfoFeatureEnum.TradindBlockedOnSession);
        items.push(TradingInfoSymbolInfoFeatureEnum.NextHoliday);
        items.push(TradingInfoSymbolInfoFeatureEnum.QuotingCurrency);
        items.push(TradingInfoSymbolInfoFeatureEnum.BettingCurrency);
        items.push(TradingInfoSymbolInfoFeatureEnum.LotSize);
        items.push(TradingInfoSymbolInfoFeatureEnum.BetSize);
        items.push(TradingInfoSymbolInfoFeatureEnum.ContractMultiplier);
        items.push(TradingInfoSymbolInfoFeatureEnum.MinimalLot); // group items
        items.push(TradingInfoSymbolInfoFeatureEnum.MaximumLot); // group items
        items.push(TradingInfoSymbolInfoFeatureEnum.MaximumLotInCash);
        items.push(TradingInfoSymbolInfoFeatureEnum.LotStep);
        items.push(TradingInfoSymbolInfoFeatureEnum.BetStep);
        items.push(TradingInfoSymbolInfoFeatureEnum.HighLimit);
        items.push(TradingInfoSymbolInfoFeatureEnum.LowLimit);
        items.push(TradingInfoSymbolInfoFeatureEnum.NormalMarketSize);
        items.push(TradingInfoSymbolInfoFeatureEnum.MaxPositionQuantity); // group items
        items.push(TradingInfoSymbolInfoFeatureEnum.MaxPositionValue);
        items.push(TradingInfoSymbolInfoFeatureEnum.AllowShortPositions); // group items
        items.push(TradingInfoSymbolInfoFeatureEnum.AllowOvernightTrading);
        items.push(TradingInfoSymbolInfoFeatureEnum.TickSizeTickCoast); // group items
        items.push(TradingInfoSymbolInfoFeatureEnum.HighLimitWarning);
        items.push(TradingInfoSymbolInfoFeatureEnum.LowLimitWarning);
        // items.push(TradingInfoSymbolInfoFeatureEnum.InstrumentAdditionalInfo); //????
        items.push(TradingInfoSymbolInfoFeatureEnum.Etb);
        items.push(TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TRADINGUNIT);
        items.push(TradingInfoSymbolInfoFeatureEnum.EXTFIELD_PRICEQUOTEUNIT);
        items.push(TradingInfoSymbolInfoFeatureEnum.EXTFIELD_DELIVERYUNIT);
        items.push(TradingInfoSymbolInfoFeatureEnum.EXTFIELD_LOTSIZE);
        items.push(TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODSTARTDATE);
        items.push(TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODENDDATE);
        return items;
    }
    getMarginRequirementsItems(currentInstrument) {
        let items = [];
        items = items.concat(this.addMaintenanceItems(currentInstrument));
        return items;
    }
    getFeesItems(currentInstrument, account) {
        var _a;
        const items = [];
        const itemsForTable = CommissionPlan.GetFeeItems(currentInstrument, account);
        const feeCurrency = CommissionItem.GetCommissionCurrency(account, currentInstrument);
        const allS = CommissionOperationType.ALL;
        const suff = CommissionItem.OperationTypeSuffix;
        for (let i = 0; i < itemsForTable.length; i++) {
            const keyArr = itemsForTable[i].split(commisionSplitter);
            const key = itemsForTable[i];
            const locKey = keyArr.length === 4 && keyArr[1] !== allS.toString() ? keyArr[0] + suff[keyArr[1]] : keyArr[0]; // имя которое отображается в таблице составное: имя + CommissionOperationType, если тип ALL то скипаем вторую часть, иначе добавляем
            // if (exFeeTypeName == locKey)
            //     displayFeeTypeName = ""; // если у предыдущей строки имя такое же не показываем
            const value = (_a = CommissionPlan.TryGetCommissionDataValue(feeCurrency, key, currentInstrument, account)) !== null && _a !== void 0 ? _a : '';
            items.push([Resources.getResource(locKey), value]);
        }
        const hasSwapBuy = currentInstrument.SwapBuy !== 0;
        const hasSwapSell = currentInstrument.SwapSell !== 0;
        if (currentInstrument.ShortSwapInfo !== '' && (hasSwapBuy || hasSwapSell)) {
            items.push([Resources.getResource('InstrumentDetailsPanel.SwapDetails'), currentInstrument.ShortSwapInfo]);
        }
        if (hasSwapBuy || hasSwapSell) {
            items.push([
                Resources.getResource('InstrumentDetailsPanel.SwapBuy'),
                this.getSwapBuySell(currentInstrument.SwapBuy, currentInstrument.SwapType)
            ]);
            items.push([
                Resources.getResource('InstrumentDetailsPanel.SwapSell'),
                this.getSwapBuySell(currentInstrument.SwapSell, currentInstrument.SwapType)
            ]);
        }
        const custodialFeeData = DataCache.CustodialPlans.GetCustodialDataByInstrument(account.CustodialPlanId, currentInstrument);
        if (custodialFeeData !== null) {
            const custodialFeeName = Resources.getResource('InstrumentDetailsPanel.CustodialFee');
            const custodialFeeValue = custodialFeeData.FormattedValue();
            items.push([custodialFeeName, custodialFeeValue]);
        }
        const shortPositionInterest = this.GetShortPositionInterest(currentInstrument);
        if (isValidString(shortPositionInterest)) {
            items.push([Resources.getResource('InstrumentDetailsPanel.ShortPostionInterest'), shortPositionInterest]);
        }
        const historicalLongSwap = this.getHistoricalSwap(currentInstrument, 'Latest historical long swap');
        if (historicalLongSwap !== null) {
            const historicalLongSwapName = Resources.getResource('InstrumentDetailsPanel.Latest historical long swap');
            const historicalLongSwapValue = historicalLongSwap + ' ' + Resources.getResource('InstrumentDetailsPanel.HistoricalSwap.points');
            items.push([historicalLongSwapName, historicalLongSwapValue]);
        }
        const historicalShortSwap = this.getHistoricalSwap(currentInstrument, 'Latest historical short swap');
        if (historicalShortSwap !== null) {
            const historicalShortSwapName = Resources.getResource('InstrumentDetailsPanel.Latest historical short swap');
            const historicalShortSwapValue = historicalShortSwap + ' ' + Resources.getResource('InstrumentDetailsPanel.HistoricalSwap.points');
            items.push([historicalShortSwapName, historicalShortSwapValue]);
        }
        const historicalLastSwap = this.getHistoricalSwap(currentInstrument, 'Historical swap last updated on');
        if (historicalLastSwap !== null) {
            const historicalLastSwapName = Resources.getResource('InstrumentDetailsPanel.Historical swap last updated on');
            const historicalLastSwapValue = historicalLastSwap;
            items.push([historicalLastSwapName, historicalLastSwapValue]);
        }
        return items;
    }
    getSessionItems(currentInstrument) {
        const items = [];
        const dayPeriods = currentInstrument.Session.GetTodayDayPeriods();
        for (let i = 0, len = dayPeriods.length; i < len; i++) {
            const dayPeriod = dayPeriods[i];
            const sessTypeName = this.GetTradeSessionTypeName(dayPeriod);
            const key = i.toString() + '.' + sessTypeName;
            const sessName = this.TradeSessionValue(key, currentInstrument, false, true);
            const sessionValue = this.TradeSessionValue(key, currentInstrument, true, false);
            items.push([sessName, sessionValue]);
        }
        return items;
    }
    valideGeneralInfoItem(item, instrument) {
        var _a, _b;
        const locKey = this.getGeneralInfoFeatureKey(item);
        if (Resources.isHidden(locKey)) {
            return false;
        }
        switch (item) {
            case GeneralSymbolInfoFeatureEnum.Underlier:
            case GeneralSymbolInfoFeatureEnum.ContactMonth:
            case GeneralSymbolInfoFeatureEnum.FirstTradeDate:
                return (instrument.InstrType === InstrumentTypes.OPTIONS ||
                    instrument.InstrType === InstrumentTypes.FUTURES ||
                    instrument.InstrType === InstrumentTypes.CFD_FUTURES);
            case GeneralSymbolInfoFeatureEnum.DeliveryMethod:
                return (instrument.InstrType === InstrumentTypes.FUTURES ||
                    instrument.InstrType === InstrumentTypes.CFD_FUTURES ||
                    instrument.InstrType === InstrumentTypes.FORWARD ||
                    instrument.InstrType === InstrumentTypes.SPOT);
            case GeneralSymbolInfoFeatureEnum.LastTradeDate:
                return (instrument.InstrType === InstrumentTypes.OPTIONS ||
                    instrument.InstrType === InstrumentTypes.FUTURES ||
                    instrument.InstrType === InstrumentTypes.CFD_FUTURES ||
                    instrument.InstrType === InstrumentTypes.FORWARD);
            case GeneralSymbolInfoFeatureEnum.NoticeDate:
                return (instrument.InstrType === InstrumentTypes.FUTURES ||
                    instrument.InstrType === InstrumentTypes.CFD_FUTURES);
            case GeneralSymbolInfoFeatureEnum.SettlementDate:
                return !!instrument.SettlementDate;
            case GeneralSymbolInfoFeatureEnum.TradingBalance:
                return instrument.InstrType !== InstrumentTypes.INDICIES;
            case GeneralSymbolInfoFeatureEnum.StrikePrice:
                return instrument.InstrType === InstrumentTypes.OPTIONS;
            case GeneralSymbolInfoFeatureEnum.CouponRate:
            case GeneralSymbolInfoFeatureEnum.CouponCycle:
            case GeneralSymbolInfoFeatureEnum.Ytm:
            case GeneralSymbolInfoFeatureEnum.AccruedInterest:
            case GeneralSymbolInfoFeatureEnum.DaysPerMonth:
            case GeneralSymbolInfoFeatureEnum.DaysPerYear:
                return instrument.InstrType === InstrumentTypes.FIXED_INCOME;
            case GeneralSymbolInfoFeatureEnum.PrevPaymentDate:
            case GeneralSymbolInfoFeatureEnum.NextPaymentDate:
                return (instrument.InstrType === InstrumentTypes.FIXED_INCOME && instrument.CouponCycle !== CouponCycle.Zero);
            case GeneralSymbolInfoFeatureEnum.Yield:
                return instrument.InstrType === InstrumentTypes.TBILL;
            case GeneralSymbolInfoFeatureEnum.Asset: {
                if (instrument.HideAsset) {
                    return false;
                }
                if (instrument.InstrType === InstrumentTypes.ETF ||
                    instrument.InstrType === InstrumentTypes.EQUITIES ||
                    instrument.InstrType === InstrumentTypes.EQUITIES_CFD ||
                    instrument.InstrType === InstrumentTypes.CORPORATE) {
                    return instrument.Exp1 !== instrument.ShortName;
                }
                return false;
            }
            case GeneralSymbolInfoFeatureEnum.ExerciseStyle:
                return instrument.InstrType === InstrumentTypes.OPTIONS;
            case GeneralSymbolInfoFeatureEnum.Isin:
                return !!instrument.ISIN;
            case GeneralSymbolInfoFeatureEnum.FaceValue:
                return (instrument.InstrType === InstrumentTypes.FIXED_INCOME ||
                    instrument.InstrType === InstrumentTypes.TBILL);
            case GeneralSymbolInfoFeatureEnum.MaturityDate:
                if (instrument.InstrType === InstrumentTypes.FORWARD ||
                    instrument.InstrType === InstrumentTypes.FIXED_INCOME ||
                    instrument.InstrType === InstrumentTypes.TBILL ||
                    instrument.InstrType === InstrumentTypes.SPOT ||
                    instrument.InstrType === InstrumentTypes.CORPORATE) {
                    return !!instrument.MaturityDate;
                }
                return false;
            case GeneralSymbolInfoFeatureEnum.Industry:
                if (isValidString(instrument.Industry)) {
                    return true;
                }
                return isValidString((_a = instrument.ForwardBaseInstrument) === null || _a === void 0 ? void 0 : _a.Industry);
            case GeneralSymbolInfoFeatureEnum.Sector:
                if (isValidString(instrument.Sector)) {
                    return true;
                }
                return isValidString((_b = instrument.ForwardBaseInstrument) === null || _b === void 0 ? void 0 : _b.Sector);
            case GeneralSymbolInfoFeatureEnum.ExtSmFlag:
                return Object.getOwnPropertyNames(instrument.GetExtSMFlags()).length > 0;
            default:
                return true;
        }
    }
    validateTradingInfoItem(item, instrument, account, productType) {
        const locKey = this.getTradingInfoFeatureKey(item);
        if (Resources.isHidden(locKey)) {
            return false;
        }
        switch (item) {
            case TradingInfoSymbolInfoFeatureEnum.DeliveryStatus:
                return (instrument.InstrType === InstrumentTypes.FUTURES ||
                    instrument.InstrType === InstrumentTypes.CFD_FUTURES ||
                    instrument.InstrType === InstrumentTypes.OPTIONS ||
                    instrument.InstrType === InstrumentTypes.SPOT);
            case TradingInfoSymbolInfoFeatureEnum.LotSize:
                return (instrument.InstrType === InstrumentTypes.FIXED_INCOME ||
                    instrument.InstrType === InstrumentTypes.TBILL);
            case TradingInfoSymbolInfoFeatureEnum.BettingCurrency:
            case TradingInfoSymbolInfoFeatureEnum.BetSize:
            case TradingInfoSymbolInfoFeatureEnum.BetStep:
                return instrument.InstrType === InstrumentTypes.SPREADBET;
            case TradingInfoSymbolInfoFeatureEnum.LotStep:
                return instrument.InstrType !== InstrumentTypes.SPREADBET;
            case TradingInfoSymbolInfoFeatureEnum.ContractMultiplier:
                if (instrument.InstrType === InstrumentTypes.FUTURES ||
                    instrument.InstrType === InstrumentTypes.CFD_FUTURES ||
                    instrument.InstrType === InstrumentTypes.OPTIONS ||
                    instrument.InstrType === InstrumentTypes.SPOT) {
                    return false;
                }
                return true;
            case TradingInfoSymbolInfoFeatureEnum.Etb:
                return instrument.Level1.GetETB() !== Level1Calculator.NAString;
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TRADINGUNIT:
                return !!instrument.GetExtTradingUnit();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_PRICEQUOTEUNIT:
                return !!instrument.GetExtPriceQuoteUnit();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_DELIVERYUNIT:
                return !!instrument.GetExtDeliveryUnit();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_LOTSIZE:
                return !!instrument.GetExtLotSize();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODSTARTDATE:
                return !!instrument.GetExtTenderPeriodStartDate();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODENDDATE:
                return !!instrument.GetExtTenderPeriodEndDate();
            case TradingInfoSymbolInfoFeatureEnum.ProductType: {
                const apt = instrument.RiskSettings.RiskPlanSettings.availableProductTypes;
                if (apt.length === 1 && apt[0] === ProductType.General) {
                    return false;
                }
                return true;
            }
            case TradingInfoSymbolInfoFeatureEnum.NextHoliday:
                return !!instrument.Session.GetNextHoliday();
            case TradingInfoSymbolInfoFeatureEnum.HighLimit:
                return !isNaN(instrument.HightLimit);
            case TradingInfoSymbolInfoFeatureEnum.LowLimit:
                return !isNaN(instrument.LowLimit);
            case TradingInfoSymbolInfoFeatureEnum.HighLimitWarning:
            case TradingInfoSymbolInfoFeatureEnum.LowLimitWarning:
                return !!instrument.WarningForChangeFromLastPrice;
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionValue: {
                const maxPositionValue = instrument.getMaxPositionValue(productType, account);
                return isValidNumber(maxPositionValue) && maxPositionValue !== -1;
            }
            default:
                return true;
        }
    }
    getGeneralInfoFeatureString(feature, currentInstrument) {
        switch (feature) {
            case GeneralSymbolInfoFeatureEnum.SymbolName:
                return currentInstrument.DisplayName();
            case GeneralSymbolInfoFeatureEnum.SymbolDescription:
                return currentInstrument.DescriptionValue();
            case GeneralSymbolInfoFeatureEnum.TradingExchage:
                return currentInstrument.TradingExchange;
            case GeneralSymbolInfoFeatureEnum.MarketDataExchange:
                return currentInstrument.MarketDataExchange;
            case GeneralSymbolInfoFeatureEnum.SymbolType:
                return InstrumentUtils.getInstrumentTypeStringLocalized(currentInstrument.InstrType, currentInstrument.CFD);
            case GeneralSymbolInfoFeatureEnum.Underlier:
                return currentInstrument.ForwardBaseInstrument ? currentInstrument.ForwardBaseInstrument.ShortName : '';
            case GeneralSymbolInfoFeatureEnum.DeliveryMethod:
                return currentInstrument.DeliveryMethod === Instrument.Delivery_Physical
                    ? Resources.getResource('InstrumentDetailsPanel.Physically')
                    : Resources.getResource('InstrumentDetailsPanel.Cash');
            case GeneralSymbolInfoFeatureEnum.ContactMonth:
                return DateTimeUtils.getFormattedDate(currentInstrument.ContractMonthDate);
            case GeneralSymbolInfoFeatureEnum.FirstTradeDate:
                return DateTimeUtils.getFormattedDate(currentInstrument.FirstTradeDate);
            case GeneralSymbolInfoFeatureEnum.LastTradeDate:
                return DateTimeUtils.getFormattedDate(currentInstrument.LastTradeDate);
            case GeneralSymbolInfoFeatureEnum.MaturityDate:
                return currentInstrument.MaturityDate
                    ? DateTimeUtils.getFormattedDate(currentInstrument.MaturityDate, true)
                    : StringUtils.THREE_DASHES;
            case GeneralSymbolInfoFeatureEnum.NoticeDate:
                return DateTimeUtils.getFormattedDate(currentInstrument.NoticeDate);
            case GeneralSymbolInfoFeatureEnum.SettlementDate:
                return currentInstrument.SettlementDate
                    ? DateTimeUtils.getFormattedDate(currentInstrument.SettlementDate)
                    : Resources.getResource('InstrumentDetailsPanel.None');
            case GeneralSymbolInfoFeatureEnum.TradingBalance:
                if (currentInstrument.TradingBalance === InstrumentTradingBalance.SETTLEMENT_IMMEDIATE) {
                    return Resources.getResource('InstrumentDetailsPanel.immediate');
                }
                else if (currentInstrument.TradingBalance === InstrumentTradingBalance.SETTLEMENT_T_PLUS) {
                    return Resources.getResource('InstrumentDetailsPanel.T+0');
                }
                else {
                    return 'T + ' + currentInstrument.TradingBalance.toString();
                }
            case GeneralSymbolInfoFeatureEnum.Isin:
                return currentInstrument.ISIN;
            case GeneralSymbolInfoFeatureEnum.StrikePrice:
                return currentInstrument.StrikePrice.toString();
            case GeneralSymbolInfoFeatureEnum.ExerciseStyle:
                return currentInstrument.GetLocalizedExerciseStyle();
            case GeneralSymbolInfoFeatureEnum.FaceValue:
                return currentInstrument.FaceValue.toString();
            case GeneralSymbolInfoFeatureEnum.CouponCycle:
                return Instrument.FormatCouponCycle(currentInstrument.CouponCycle);
            case GeneralSymbolInfoFeatureEnum.CouponRate:
                return currentInstrument.CouponRate + ' %';
            case GeneralSymbolInfoFeatureEnum.Ytm:
                return currentInstrument.YieldRate ? currentInstrument.YieldRate + ' %' : '0 %';
            case GeneralSymbolInfoFeatureEnum.AccruedInterest:
                return currentInstrument.formatPrice(currentInstrument.AccruedInterest) + ' ' + currentInstrument.Exp2;
            case GeneralSymbolInfoFeatureEnum.PrevPaymentDate:
                return DateTimeUtils.getFormattedDate(currentInstrument.PrevPaymentDate, true);
            case GeneralSymbolInfoFeatureEnum.NextPaymentDate:
                return DateTimeUtils.getFormattedDate(currentInstrument.NextPaymentDate);
            case GeneralSymbolInfoFeatureEnum.DaysPerMonth:
                return this.getDaysPerFormatted(currentInstrument.DaysPerMonth, GeneralSymbolInfoFeatureEnum.DaysPerMonth);
            case GeneralSymbolInfoFeatureEnum.DaysPerYear:
                return this.getDaysPerFormatted(currentInstrument.DaysPerYear, GeneralSymbolInfoFeatureEnum.DaysPerYear);
            case GeneralSymbolInfoFeatureEnum.Yield:
                return currentInstrument.YieldRate ? currentInstrument.YieldRate + ' %' : '0 %';
            case GeneralSymbolInfoFeatureEnum.Asset:
                return currentInstrument.Exp1;
            case GeneralSymbolInfoFeatureEnum.Industry:
                return currentInstrument.Industry
                    ? currentInstrument.Industry
                    : currentInstrument.ForwardBaseInstrument
                        ? currentInstrument.ForwardBaseInstrument.Industry
                        : '';
            case GeneralSymbolInfoFeatureEnum.Sector:
                return currentInstrument.Sector
                    ? currentInstrument.Sector
                    : currentInstrument.ForwardBaseInstrument
                        ? currentInstrument.ForwardBaseInstrument.Sector
                        : '';
            case GeneralSymbolInfoFeatureEnum.ExtSmFlag:
            default:
                return `General item ${feature} not found in InstrumentFeatureHelper::getGeneralInfoFeatureString`;
        }
    }
    getTradingInfoFeature(feature, currentInstrument, account, productType) {
        var _a, _b;
        switch (feature) {
            case TradingInfoSymbolInfoFeatureEnum.TradingStatus:
                return InstrumentUtils.GetTradingStatus(currentInstrument);
            case TradingInfoSymbolInfoFeatureEnum.AllowedOperations: {
                const allowedOperations = this.getAllowedOperations(currentInstrument);
                if (allowedOperations != null) {
                    return allowedOperations;
                }
                else {
                    return Resources.getResource('panel.watchlist.cell.Not allow');
                }
            }
            case TradingInfoSymbolInfoFeatureEnum.AllowedOrderTypes: {
                const allowedOrderTypes = this.GetAllowedOrderTypes(currentInstrument);
                if (allowedOrderTypes != null) {
                    return allowedOrderTypes;
                }
                else {
                    return Resources.getResource('panel.watchlist.cell.Not allow');
                }
            }
            case TradingInfoSymbolInfoFeatureEnum.ProductType: {
                const availableProductTypes = currentInstrument.RiskSettings.RiskPlanSettings.availableProductTypes;
                if (availableProductTypes.length === 2 && productType == null) {
                    return (Resources.getResource('ProductType.Intraday') +
                        '/' +
                        Resources.getResource('ProductType.Delivery'));
                }
                else {
                    if (productType == null)
                        productType = availableProductTypes[0];
                    return productType != null
                        ? InstrumentUtils.GetLocalizedProductType(currentInstrument, productType)
                        : '';
                }
            }
            case TradingInfoSymbolInfoFeatureEnum.DeliveryStatus:
                return currentInstrument.LastTradeDate < DateTimeUtils.DateTimeUtcNow()
                    ? StringUtils.THREE_DASHES // GetDeliveryStatus(currentInstrument.DeliveryStatus, sett) ???????????????
                    : StringUtils.THREE_DASHES;
            case TradingInfoSymbolInfoFeatureEnum.CurrentSession:
                return currentInstrument.ExchangeSessionName;
            case TradingInfoSymbolInfoFeatureEnum.SessionStatus: {
                const tradeSessionStatus = DataCache.GetTradeSessionStatus(currentInstrument.GetTradeSessionStatusId());
                return tradeSessionStatus != null ? tradeSessionStatus.Name : '';
            }
            case TradingInfoSymbolInfoFeatureEnum.NextHoliday: {
                const holiday = currentInstrument.Session.GetNextHoliday();
                return holiday != null ? holiday.GetFormattedDate() + ' (' + holiday.name + ')' : '';
            }
            case TradingInfoSymbolInfoFeatureEnum.QuotingCurrency:
                return currentInstrument.Exp2;
            case TradingInfoSymbolInfoFeatureEnum.BettingCurrency:
                return (_a = currentInstrument.CalculationCurrency) !== null && _a !== void 0 ? _a : '';
            case TradingInfoSymbolInfoFeatureEnum.BetSize:
                return isNullOrUndefined(currentInstrument.BetSize)
                    ? ''
                    : MathUtils.formatValueWithEps(currentInstrument.BetSize);
            case TradingInfoSymbolInfoFeatureEnum.BetStep:
                return isNullOrUndefined(currentInstrument.BetStep)
                    ? ''
                    : MathUtils.formatValueWithEps(currentInstrument.BetStep);
            case TradingInfoSymbolInfoFeatureEnum.LotSize:
                if (currentInstrument.InstrType === InstrumentTypes.FUTURES ||
                    currentInstrument.InstrType === InstrumentTypes.CFD_FUTURES ||
                    currentInstrument.InstrType === InstrumentTypes.OPTIONS ||
                    currentInstrument.InstrType ===
                        InstrumentTypes.SPREADBET /* && key.indexOf(SymbolInfoPanel.KEY_TickCoast) !== -1 */) {
                    //! !!!
                    let tickCoast = currentInstrument.FuturesTickCoast;
                    if (tickCoast < 0) {
                        tickCoast = currentInstrument.LotSize * currentInstrument.PointSize;
                    }
                    // if (key.indexOf(SymbolInfoPanel.KEY_TickCoast) !== -1) {
                    //     let commisionKey = key.split(SymbolInfoPanel.commisionSplitter);
                    //     if (commisionKey.length == 6) {
                    //         return SymbolInfoPanel.GetFeeString(commisionKey[1], commisionKey[2],
                    //             commisionKey[3], SymbolInfoPanel.ConvertToBoolean(commisionKey[4]), SymbolInfoPanel.ConvertToBoolean(commisionKey[5]), sett, SymbolInfoPanel.FeeStringVariable.Price);
                    //     }
                    //     return tickCoast.toString();
                    // }
                    // if (key == SymbolInfoPanel.KEY_LotSize && currentInstrument.QuotingType == QuotingType.LotSize)
                    //     return currentInstrument.LotSize.toString() + (currentInstrument.InstrType == InstrumentTypes.FOREX ? " " + currentInstrument.Exp2 : "");
                    return StringUtils.THREE_DASHES;
                }
                else {
                    // let stLotSize = currentInstrument.LotSize.toString();
                    // if (currentInstrument.isForexSymbol())
                    //     stLotSize += " " + currentInstrument.Exp1;
                    // if (key == SymbolInfoPanel.KEY_LotSize)
                    //     return stLotSize;
                    // //х.з. зачем так делать, почему не прислать в мессадже тик кост если нужен
                    // //double tickCoast = currentInstrument.LotSize * (double)currentInstrument.PointSize;
                    // let tickCoast = currentInstrument.FuturesTickCoast;
                    // if (key.indexOf(SymbolInfoPanel.KEY_TickCoast) !== -1)
                    //     return tickCoast.toString();
                    return StringUtils.THREE_DASHES;
                }
            case TradingInfoSymbolInfoFeatureEnum.ContractMultiplier:
                return currentInstrument.ContractMultiplier.toString() + ' ' + currentInstrument.Exp1;
            case TradingInfoSymbolInfoFeatureEnum.LotStep:
                return MathUtils.formatValueWithEps(currentInstrument.getLotStep(productType, account));
            case TradingInfoSymbolInfoFeatureEnum.HighLimit:
                return currentInstrument.Limits.HighLimitPriceByMeasureString();
            case TradingInfoSymbolInfoFeatureEnum.LowLimit:
                return currentInstrument.Limits.LowLimitPriceByMeasureString();
            case TradingInfoSymbolInfoFeatureEnum.AllowOvernightTrading:
                return this.GetLocalizedAllowShortPosition(currentInstrument.RiskSettings.isOvernightTradingsAllowed());
            case TradingInfoSymbolInfoFeatureEnum.NormalMarketSize:
                return currentInstrument.Level1.getNormalMarketSize;
            case TradingInfoSymbolInfoFeatureEnum.Etb:
                return currentInstrument.Level1.GetETB();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TRADINGUNIT:
                return currentInstrument.GetExtTradingUnit();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_PRICEQUOTEUNIT:
                return currentInstrument.GetExtPriceQuoteUnit();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_DELIVERYUNIT:
                return currentInstrument.GetExtDeliveryUnit();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_LOTSIZE:
                return currentInstrument.GetExtLotSize();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODSTARTDATE:
                return currentInstrument.GetExtTenderPeriodStartDate();
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODENDDATE:
                return currentInstrument.GetExtTenderPeriodEndDate();
            case TradingInfoSymbolInfoFeatureEnum.TradindBlockedOnSession:
                if (currentInstrument.Session.BlockTrading || currentInstrument.Session.CurrentSessionPeriod == null) {
                    return Resources.getResource('InstrumentDetailsPanel.Yes');
                }
                else {
                    return Resources.getResource('InstrumentDetailsPanel.No');
                }
            case TradingInfoSymbolInfoFeatureEnum.MaximumLotInCash: {
                const maxLotInCash = currentInstrument.getMaxLotInCash();
                if (maxLotInCash > 0 && maxLotInCash !== NumericUtils.MAXVALUE) {
                    const asset = DataCache.GetAssetByName(currentInstrument.CalculationCurrency);
                    const formattedPrice = (_b = asset === null || asset === void 0 ? void 0 : asset.formatPrice(maxLotInCash, true)) !== null && _b !== void 0 ? _b : maxLotInCash.toString();
                    return CurrencyUtils.FormatWithCurrencySymbol(formattedPrice, currentInstrument.CalculationCurrency);
                }
                else {
                    return null;
                }
            } /* group items */
            case TradingInfoSymbolInfoFeatureEnum.MinimalLot: // addMinimalLotItems
            case TradingInfoSymbolInfoFeatureEnum.MaximumLot: // addMaximalLotItems
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionQuantity: // addMaxPositionQtyItems
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionValue:
            case TradingInfoSymbolInfoFeatureEnum.AllowShortPositions: // addMaxPositionQtyItems
            case TradingInfoSymbolInfoFeatureEnum.TickSizeTickCoast: // addTickSizeTickCoastItems
            case TradingInfoSymbolInfoFeatureEnum.HighLimitWarning:
            case TradingInfoSymbolInfoFeatureEnum.LowLimitWarning:
            default:
                return `Trading item ${feature} not found in InstrumentFeatureHelper::getGeneralInfoFeatureString`;
        }
    }
    isTradingInfoLongItem(feature) {
        switch (feature) {
            case TradingInfoSymbolInfoFeatureEnum.MinimalLot:
            case TradingInfoSymbolInfoFeatureEnum.MaximumLot:
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionQuantity:
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionValue:
            case TradingInfoSymbolInfoFeatureEnum.AllowShortPositions:
            case TradingInfoSymbolInfoFeatureEnum.TickSizeTickCoast:
                return true;
            default:
                return false;
        }
    }
    getTradingInfoFeatureItems(feature, currentInstrument, account) {
        const items = [];
        if (!this.isTradingInfoLongItem(feature)) {
            return items;
        }
        if (feature === TradingInfoSymbolInfoFeatureEnum.TickSizeTickCoast) {
            return this.addTickSizeCostItems(currentInstrument);
        }
        return this.ProcessItemByProductType(feature, currentInstrument, account);
    }
    getGeneralInfoFeatureLocalization(feature) {
        return Resources.getResource(this.getGeneralInfoFeatureKey(feature));
    }
    getTradingInfoFeatureLocalization(feature) {
        return Resources.getResource(InstrumentFeatureHelper.getTradingInfoFeatureKey(feature));
    }
    GetInstrumentFeatureString(feature, currentInstrument) {
        switch (feature) {
            case InstrumentFeatureEnum.SwapBuy:
                if (currentInstrument.SwapBuy !== 0) {
                    return this.getSwapBuySell(currentInstrument.SwapBuy, currentInstrument.SwapType);
                }
                else {
                    return Resources.getResource('InstrumentDetailsPanel.None');
                }
            case InstrumentFeatureEnum.SwapSell:
                if (currentInstrument.SwapSell !== 0) {
                    return this.getSwapBuySell(currentInstrument.SwapSell, currentInstrument.SwapType);
                }
                else {
                    return Resources.getResource('InstrumentDetailsPanel.None');
                }
            default:
                return 'Item not found in InstrumentFeatureHelper';
        }
    }
    GetTradeSessionTypeName(dayPeriod) {
        const SESSION_DAY_PERIOD_PREOPEN = 'InstrumentDetailsPanel.PREOPEN';
        const SESSION_DAY_PERIOD_MAIN = 'InstrumentDetailsPanel.MAIN';
        const SESSION_DAY_PERIOD_POST_CLOSE = 'InstrumentDetailsPanel.POSTCLOSE';
        const SESSION_DAY_PERIOD_BEFORE_MARKET = 'InstrumentDetailsPanel.BEFORE_MARKET';
        const SESSION_DAY_PERIOD_AFTER_MARKET = 'InstrumentDetailsPanel.AFTER_MARKET';
        switch (dayPeriod) {
            case DayPeriods.PRE_OPEN:
                return SESSION_DAY_PERIOD_PREOPEN;
            case DayPeriods.MAIN:
                return SESSION_DAY_PERIOD_MAIN;
            case DayPeriods.POST_CLOSE:
                return SESSION_DAY_PERIOD_POST_CLOSE;
            case DayPeriods.BEFORE_MARKET:
                return SESSION_DAY_PERIOD_BEFORE_MARKET;
            case DayPeriods.AFTER_MARKET:
                return SESSION_DAY_PERIOD_AFTER_MARKET;
        }
        return '';
    }
    TradeSessionValue(key, currentInstrument, showSessionTime, showSessionName) {
        let sessionPeriodIndex = -1;
        if (key.includes('.')) {
            sessionPeriodIndex = Number(key.split('.')[0]);
        }
        return SessionUtils.GetSessionPeriodTimeForInstrumentDetails(currentInstrument.Session, sessionPeriodIndex, showSessionTime, showSessionName);
    }
    GetLeverageItemsFromProductType(instrument, productType) {
        const items = [];
        const leverageLength = this.GetLeverageLengthFromProductType(instrument, productType);
        for (let i = 0; i < leverageLength; i++) {
            const leverageKey = this.getMarginLocalizationKeyProductType(MarginEnum.KEY_Leverage, instrument, productType);
            const leverageName = Resources.getResource(leverageKey);
            const margin = RiskPlan.GetMargin(instrument, productType, i);
            const leverageValue = margin.Leverage;
            const leverageStr = OrderUtils.GetFormattedLeverage(leverageValue);
            items.push([leverageName, leverageStr]);
        }
        return items;
    }
    GetLeverageLengthFromProductType(instrument, productType) {
        const isHedgingNettingType = instrument.isHedgingNettingType();
        const leverages = RiskPlan.GetLeverages(instrument, productType);
        const itemsN = leverages ? (isHedgingNettingType || leverages.length === 0 ? leverages.length : 1) : 0;
        return itemsN;
    }
    GetShortPositionInterest(currentInstrument) {
        const { ShortPositionInterest } = currentInstrument;
        return isValidNumber(ShortPositionInterest) && ShortPositionInterest !== 0 ? `${ShortPositionInterest} %` : '';
    }
    GetLocalizedAllowShortPosition(isAllowed) {
        return Resources.getResource(isAllowed
            ? 'InstrumentDetailsPanel.Allow short positions'
            : 'InstrumentDetailsPanel.Not allow short positions');
    }
    getTradingInfoFeatureKey(feature) {
        switch (feature) {
            case TradingInfoSymbolInfoFeatureEnum.TradingStatus:
                return 'InstrumentDetailsPanel.TradingStatus';
            case TradingInfoSymbolInfoFeatureEnum.AllowedOperations:
                return 'InstrumentDetailsPanel.AllowedOperations';
            case TradingInfoSymbolInfoFeatureEnum.AllowedOrderTypes:
                return 'InstrumentDetailsPanel.AllowedOrderTypes';
            case TradingInfoSymbolInfoFeatureEnum.ProductType:
                return 'InstrumentDetailsPanel.ProductType';
            case TradingInfoSymbolInfoFeatureEnum.DeliveryStatus:
                return 'InstrumentDetailsPanel.DeliveryStatus';
            case TradingInfoSymbolInfoFeatureEnum.CurrentSession:
                return 'InstrumentDetailsPanel.CurrentSession';
            case TradingInfoSymbolInfoFeatureEnum.SessionStatus:
                return 'InstrumentDetailsPanel.SessionStatus';
            case TradingInfoSymbolInfoFeatureEnum.NextHoliday:
                return 'InstrumentDetailsPanel.KEYNextHoliday';
            case TradingInfoSymbolInfoFeatureEnum.QuotingCurrency:
                return 'InstrumentDetailsPanel.QuotiongCurrency';
            case TradingInfoSymbolInfoFeatureEnum.BettingCurrency:
                return 'InstrumentDetailsPanel.BettingCurrency';
            case TradingInfoSymbolInfoFeatureEnum.LotSize:
                return 'InstrumentDetailsPanel.LotSize';
            case TradingInfoSymbolInfoFeatureEnum.ContractMultiplier:
                return 'InstrumentDetailsPanel.ContractMultiplier';
            case TradingInfoSymbolInfoFeatureEnum.LotStep:
                return 'InstrumentDetailsPanel.LotStep';
            case TradingInfoSymbolInfoFeatureEnum.BetSize:
                return 'InstrumentDetailsPanel.BetSize';
            case TradingInfoSymbolInfoFeatureEnum.BetStep:
                return 'InstrumentDetailsPanel.BetStep';
            case TradingInfoSymbolInfoFeatureEnum.HighLimit:
                return 'InstrumentDetailsPanel.HiLimit';
            case TradingInfoSymbolInfoFeatureEnum.LowLimit:
                return 'InstrumentDetailsPanel.LowLimit';
            case TradingInfoSymbolInfoFeatureEnum.AllowOvernightTrading:
                return 'InstrumentDetailsPanel.AllowOvernightTrading';
            case TradingInfoSymbolInfoFeatureEnum.NormalMarketSize:
                return 'InstrumentDetailsPanel.NormalMarketSize';
            case TradingInfoSymbolInfoFeatureEnum.Etb:
                return 'InstrumentDetailsPanel.ETB';
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TRADINGUNIT:
                return 'InstrumentDetailsPanel.ExtField.TradingUnit';
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_PRICEQUOTEUNIT:
                return 'InstrumentDetailsPanel.ExtField.PriceQuoteUnit';
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_DELIVERYUNIT:
                return 'InstrumentDetailsPanel.ExtField.DeliveryUnit';
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_LOTSIZE:
                return 'InstrumentDetailsPanel.ExtField.LotSize';
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODSTARTDATE:
                return 'InstrumentDetailsPanel.ExtField.TenderPeriodStartDate';
            case TradingInfoSymbolInfoFeatureEnum.EXTFIELD_TENDERPERIODENDDATE:
                return 'InstrumentDetailsPanel.ExtField.TenderPeriodEndDate';
            case TradingInfoSymbolInfoFeatureEnum.TradindBlockedOnSession:
                return 'InstrumentDetailsPanel.TradindBlockedOnSession';
            // group items
            case TradingInfoSymbolInfoFeatureEnum.MinimalLot:
                return 'InstrumentDetailsPanel.MinimalLot';
            case TradingInfoSymbolInfoFeatureEnum.MaximumLot:
                return 'InstrumentDetailsPanel.MaximumLot';
            case TradingInfoSymbolInfoFeatureEnum.MaximumLotInCash:
                return 'InstrumentDetailsPanel.MaximumLotInCash';
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionQuantity:
                return 'InstrumentDetailsPanel.MaxPositionQtyPerSymbol';
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionValue:
                return 'InstrumentDetailsPanel.MaxPositionValuePerSymbol';
            case TradingInfoSymbolInfoFeatureEnum.AllowShortPositions:
                return 'InstrumentDetailsPanel.AllowShortPositions';
            case TradingInfoSymbolInfoFeatureEnum.TickSizeTickCoast:
                return 'InstrumentDetailsPanel.TickSize'; // ?? how to hide it? - TickSize/TickCoast/LotSize
            case TradingInfoSymbolInfoFeatureEnum.HighLimitWarning:
                return 'InstrumentDetailsPanel.HighLimitWarning';
            case TradingInfoSymbolInfoFeatureEnum.LowLimitWarning:
                return 'InstrumentDetailsPanel.LowLimitWarning';
            default:
                return `Trading item ${feature} not found in InstrumentFeatureHelper::getGeneralInfoFeatureString`;
        }
    }
    getAllowedOperations(currentInstrument) {
        const comma = ', ';
        let allow = '';
        if (!currentInstrument) {
            return allow;
        }
        const tradingMode = currentInstrument.TradingMode;
        if (tradingMode === TradingMode.FullyOpen || tradingMode === TradingMode.LiquidationOnly) {
            const allowedOperations = Instrument.GetAllowedOperations(currentInstrument);
            const decreaseOnlyPositionCount = Instrument.IsDecreaseOnlyPositionCount(currentInstrument);
            if (allowedOperations & SessionOperations.Open && this.GetAllowedOrderTypes(currentInstrument)) {
                allow += Resources.getResource('panel.watchlist.cell.sending') + comma;
            }
            if (allowedOperations & SessionOperations.Modify) {
                allow += Resources.getResource('panel.watchlist.cell.modify') + comma;
            }
            if (allowedOperations & SessionOperations.Cancel) {
                allow += Resources.getResource('panel.watchlist.cell.cancel') + comma;
            }
            if (decreaseOnlyPositionCount) {
                allow += Resources.getResource('InstrumentDetailsPanel.onlyCloseOrders') + comma;
            }
        }
        else if (tradingMode === TradingMode.TradingHalt) {
            allow = Resources.getResource('panel.watchlist.cell.cancel');
        }
        if (allow.endsWith(comma)) {
            allow = allow.substring(0, allow.length - comma.length);
        }
        return allow;
    }
    GetAllowedOrderTypes(instrument) {
        const comma = ', ';
        let allow = '';
        const tradingMode = instrument.TradingMode;
        if (tradingMode === TradingMode.FullyOpen ||
            tradingMode === TradingMode.TradingHalt ||
            tradingMode === TradingMode.LiquidationOnly) {
            const supportedParamObj = new OrderTypeBaseParameters(instrument);
            const OrderTypes = DataCache.OrderParameterContainer.OrderTypes;
            for (const orderType in OrderTypes) {
                const oT = OrderTypes[orderType];
                if (oT.IsSupported(supportedParamObj)) {
                    allow += Resources.getResource(oT.localizationKey()) + comma;
                }
            }
        }
        if (allow.endsWith(comma)) {
            allow = allow.substring(0, allow.length - comma.length);
        }
        return allow;
    }
    getSwapBuySell(price, swapType) {
        switch (swapType) {
            case SwapType.SWAP_IN_CURRENCY:
                return price + ' ' + DataCache.baseCurrency;
            case SwapType.SWAP_IN_PERCENT:
                return price + ' %';
            case SwapType.SWAP_IN_PIPS:
                return price + ' ' + Resources.getResource('general.trading.pips');
            default:
                return '';
        }
    }
    addMaintenanceItems(currentInstrument) {
        return RiskPlan.GetAvailableProductTypes(null, currentInstrument).reduce((items, productType) => {
            return items.concat(this.addMaintenanceItemsByProductType(currentInstrument, productType));
        }, []);
    }
    addMaintenanceItemsByProductType(instrument, productType) {
        let items = [];
        if (!RiskPlan.UseOvernightMargin(instrument, productType) &&
            !RiskPlan.UseLongShortMargin(instrument, productType)) {
            items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_Margin, instrument, productType));
        }
        else if (RiskPlan.UseOvernightMargin(instrument, productType) &&
            !RiskPlan.UseLongShortMargin(instrument, productType)) {
            items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginDay, instrument, productType));
            items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginOvernight, instrument, productType));
        }
        else if (!RiskPlan.UseOvernightMargin(instrument, productType) &&
            RiskPlan.UseLongShortMargin(instrument, productType)) {
            items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginBuy, instrument, productType));
            items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginSell, instrument, productType));
        }
        else if (RiskPlan.UseOvernightMargin(instrument, productType) &&
            RiskPlan.UseLongShortMargin(instrument, productType)) {
            if (RiskPlan.GetCalculateMarginTypeNullable(instrument, productType) !== MarginTypes.MARGIN_FULL) {
                items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginDayBuy, instrument, productType));
                items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginDaySell, instrument, productType));
                items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginOvernightBuy, instrument, productType));
                items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_MarginOvernightSell, instrument, productType));
            }
            else {
                items = items.concat(this.getMarginRequirementsItemsFromProductType(MarginEnum.KEY_Margin, instrument, productType));
            }
        }
        items = items.concat(this.GetLeverageItemsFromProductType(instrument, productType));
        return items;
    }
    getMarginRequirementsItemsFromProductType(key, instrument, productType) {
        const items = [];
        const itemsN = RiskPlan.GetMarginCoeficientListLength(instrument, productType);
        for (let i = 0; i < itemsN; i++) {
            const marginKey = this.getMarginLocalizationKeyProductType(key, instrument, productType);
            const marginName = Resources.getResource(marginKey);
            const marginValue = this.getMarginValue(key, instrument, productType, i);
            items.push([marginName, marginValue]);
        }
        return items;
    }
    getMarginValue(key, instrument, productType, tier) {
        let marginValue;
        let isOvernight = false;
        switch (key) {
            case MarginEnum.KEY_MarginDay:
            case MarginEnum.KEY_MarginBuy:
            case MarginEnum.KEY_MarginDayBuy:
            case MarginEnum.KEY_MarginSell:
            case MarginEnum.KEY_MarginDaySell:
                marginValue = RiskPlan.GetShortMargin(instrument, productType, tier);
                break;
            case MarginEnum.KEY_MarginOvernight:
            case MarginEnum.KEY_MarginOvernightBuy:
                marginValue = RiskPlan.GetOvernightMargin(instrument, productType, tier);
                isOvernight = true;
                break;
            case MarginEnum.KEY_MarginOvernightSell:
                marginValue = RiskPlan.GetOvernightShortMargin(instrument, productType, tier);
                isOvernight = true;
                break;
            case MarginEnum.KEY_Margin:
            default:
                marginValue = RiskPlan.GetMargin(instrument, productType, tier);
                break;
        }
        return this.FormatMargin(marginValue.Initial, marginValue.Warning, marginValue.Maintenance, instrument, productType, isOvernight);
    }
    getMarginLocalizationKeyProductType(key, instrument, productType) {
        const useProductType = RiskPlan.GetAvailableProductTypes(null, instrument).length > 1;
        const marginLocKey = this.getMarginLocalizationKey(key);
        if (!useProductType) {
            return marginLocKey;
        }
        const separator = '.';
        const productTypeSuffix = this.GetProductTypeSuffix(productType);
        return marginLocKey + separator + productTypeSuffix;
    }
    getMarginLocalizationKey(key) {
        switch (key) {
            case MarginEnum.KEY_Margin:
                return 'InstrumentDetailsPanel.Margin';
            case MarginEnum.KEY_MarginBuy:
                return 'InstrumentDetailsPanel.MarginBuy';
            case MarginEnum.KEY_MarginSell:
                return 'InstrumentDetailsPanel.MarginSell';
            case MarginEnum.KEY_MarginDay:
                return 'InstrumentDetailsPanel.MarginDay';
            case MarginEnum.KEY_MarginDayBuy:
                return 'InstrumentDetailsPanel.MarginDayBuy';
            case MarginEnum.KEY_MarginDaySell:
                return 'InstrumentDetailsPanel.MarginDaySell';
            case MarginEnum.KEY_MarginOvernight:
                return 'InstrumentDetailsPanel.MarginOvernight';
            case MarginEnum.KEY_MarginOvernightBuy:
                return 'InstrumentDetailsPanel.MarginOvernightBuy';
            case MarginEnum.KEY_MarginOvernightSell:
                return 'InstrumentDetailsPanel.MarginOvernightSell';
            case MarginEnum.KEY_Leverage:
                return 'InstrumentDetailsPanel.Leverage';
        }
    }
    GetProductTypeSuffix(type) {
        const productTypeKeys = Enum.TakeKeysFromEnum(ProductType);
        for (const key of productTypeKeys) {
            if (ProductType[key] === type) {
                return key;
            }
        }
    }
    FormatMargin(init, warn, maint, instrument, productType, isOvernight) {
        if (!isOvernight) {
            isOvernight = false;
        }
        const isShowAllKoef = init !== maint || warn !== maint;
        const currentInstrument = instrument;
        if (currentInstrument === null) {
            return this.FormatMargin_5ARG(init, warn, maint, '', isShowAllKoef);
        }
        const exp2 = currentInstrument.Exp2;
        const marginType = RiskPlan.GetCalculateMarginTypeNullable(currentInstrument, productType);
        if (marginType === MarginTypes.MARGIN_FIXED) {
            return this.FormatMargin_5ARG(init, warn, maint, exp2, isShowAllKoef);
        }
        // instrument CCY
        if (marginType === MarginTypes.MARGIN_FULL ||
            marginType === MarginTypes.MARGIN_STOCK ||
            marginType === MarginTypes.FIXED_INCOME_PRE_PAID) {
            return '100 %';
        }
        if (marginType === MarginTypes.MARGIN_PRICE_LIMIT) {
            // Limit based.
            // #28806   MD: futures margin coeficients
            // Alex Khomenko: при типе маржи MARGIN_SETLEMENT / 7 /  инит маржин должна браться не из объекта маржи, а из лимитов, тоесть нужно складывать значения полей 400 399 (*Андрей*)
            init = currentInstrument.Limits.HightLimit + currentInstrument.Limits.LowLimit;
            // #26758 Symbol info и тип маржи Limit based.
            // сервер: значение константы FIELD_PRICE_LIMIT_MESURE:  byte PERCENTEGE = 0; byte OFFSET = 1; byte DISABLE = -1;
            let lotSize = currentInstrument.LotSize;
            if (currentInstrument.isFutureOrOption()) {
                lotSize = currentInstrument.FuturesTickCoast / currentInstrument.PointSize;
            }
            if (currentInstrument.Limits.PriceLimitMeasure === PriceLimitMeasure.PERCENTAGE_SETTL_PRICE) {
                const k = lotSize * currentInstrument.PrevSettlementPrice * 0.01;
                return this.FormatMargin_5ARG(init * k, warn * k, init * /* Возможно это опечатка */ maint * k, exp2, isShowAllKoef);
            }
            if (currentInstrument.Limits.PriceLimitMeasure === PriceLimitMeasure.OFFSET_SETTL_PRICE) {
                const k = lotSize * currentInstrument.PointSize;
                return this.FormatMargin_5ARG(init * k, warn * k, maint * k, exp2, isShowAllKoef);
            }
            return Resources.getResource('InstrumentDetailsPanel.None');
        }
        if (marginType === MarginTypes.MARGIN_PT_RISK_MODULE) {
            return Resources.getResource('InstrumentDetailsPanel.PTRM');
        }
        if (marginType === MarginTypes.MARGIN_NSE_VAR) {
            if (isOvernight && RiskPlan.GetUseVarOnlyIntraday(currentInstrument, productType)) {
                return this.FormatMargin_3ARG(init, warn, maint);
            }
            const varKoef = RiskPlan.GetVarCoeffNullable(currentInstrument, productType);
            if (RiskPlan.GetVarCoeffApplyingNullable(currentInstrument, productType) === 0) {
                return this.FormatMargin_3ARG(varKoef * init, varKoef * warn, varKoef * maint);
            }
            return this.FormatMargin_3ARG(Math.max(init, varKoef), Math.max(warn, varKoef), Math.max(maint, varKoef));
        }
        if (marginType === MarginTypes.MARGIN_NONE) {
            return '-';
        }
        if (marginType === MarginTypes.LONG_OPTIONS_FULLY_PAID_MARGIN) {
            return currentInstrument.isOptionSymbol
                ? this.FormatMarginPercentage_3ARG(1, 0, 0)
                : this.FormatMarginPercentage_3ARG(0, 0, 0);
        }
        if (marginType === MarginTypes.MARGIN_CUSTOM_FIXED) {
            const currencyStr = RiskPlan.GetCustomCurrencyStr(currentInstrument, productType);
            return this.FormatMargin_5ARG(init, warn, maint, currencyStr, isShowAllKoef);
        }
        if (marginType === MarginTypes.TIERED_BY_GROUP) {
            return Resources.getResource('InstrumentDetailsPanel.TieredByGroup');
        }
        const precisions = [
            MathUtils.getPrecision(init) - 2,
            MathUtils.getPrecision(warn) - 2,
            MathUtils.getPrecision(maint) - 2
        ]; //  95646 запоминаем исходную точность после умножения на 100 (= -2 знака ), чтобы точность не менялась в процессе арифметической операции (JS 1 <3)
        return this.FormatMargin_5ARG(init * 100, warn * 100, maint * 100, '%', isShowAllKoef, precisions);
    }
    FormatMargin_3ARG(init, warn, maint) {
        return (MathUtils.TruncateDouble(init, 2) +
            '% / ' +
            MathUtils.TruncateDouble(warn, 2) +
            '% / ' +
            MathUtils.TruncateDouble(maint, 2) +
            '%');
    }
    FormatMarginPercentage_3ARG(init, warn, maint) {
        if (init !== maint || warn !== maint) {
            return (MathUtils.TruncateDouble(init * 100, 2) +
                '% / ' +
                MathUtils.TruncateDouble(warn * 100, 2) +
                '% / ' +
                MathUtils.TruncateDouble(maint * 100, 2) +
                '%');
        }
        return MathUtils.TruncateDouble(init * 100, 2) + '%';
    }
    /// определяем какие айтемы отображать (для айтемов, количество которых зависит от ProductType)
    ProcessItemByProductType(key, instrument, account) {
        const items = [];
        const aPT = instrument.RiskSettings.RiskPlanSettings.availableProductTypes;
        for (let i = 0; i < aPT.length; i++) {
            const productType = aPT[i];
            const locKey = this.getTradingInfoLocalizationKeyProductType(key, instrument, productType);
            const keyName = Resources.getResource(locKey);
            const keyValue = this.getTradingInfoValueByProductType(key, instrument, account, productType);
            items.push([keyName, keyValue]);
        }
        return items;
    }
    PrepareTickSizeCostItems(instrument, key) {
        const items = [];
        for (let i = 1; i < instrument.VariableTickList.length; i++) {
            let rightBorder = instrument.VariableTickList[i].RightBorder;
            const includeRightBorder = i < instrument.VariableTickList.length - 1
                ? !instrument.VariableTickList[i + 1].IncludeLeftBorder
                : false;
            if (!isFinite(rightBorder)) {
                rightBorder = -1;
            }
            const lb = instrument.VariableTickList[i].LeftBorder;
            const rb = rightBorder;
            const il = instrument.VariableTickList[i].IncludeLeftBorder;
            const ir = includeRightBorder;
            if (key === TickSizeTickCoastEnum.KEY_TickCoast && instrument.isFutureOrOption()) {
                const tc = instrument.VariableTickList[i].TickCost;
                const keyName = this.getTickSizeTickCoastFormatString(lb, rb, il, ir, FormatStringEnum.Price);
                const keyValue = tc.toString();
                items.push([keyName, keyValue]);
            }
            if (key === TickSizeTickCoastEnum.KEY_TickSize) {
                const pointSize = instrument.VariableTickList[i].PointSize;
                const point = (pointSize < 1 ? parseFloat(pointSize.toFixed(10)) : pointSize).toString();
                const keyName = this.getTickSizeTickCoastFormatString(lb, rb, il, ir, FormatStringEnum.Price);
                const keyValue = point;
                items.push([keyName, keyValue]);
            }
        }
        return items;
    }
    getDaysPerFormatted(value, period) {
        const perMonth = period === GeneralSymbolInfoFeatureEnum.DaysPerMonth; // else DaysPerYear
        let result = null;
        switch (value) {
            case DaysPerType.ACTUAL:
                result = Resources.getResource('DaysPerType.Actual');
                break;
            case DaysPerType.CONST_360:
                result = perMonth ? 30 : 360;
                break;
            case DaysPerType.CONST_365:
                result = perMonth ? 30 : 365;
                break;
        }
        return result === null ? '' : result.toString();
    }
    getTradingInfoValueByProductType(key, instrument, account, productType) {
        var _a;
        switch (key) {
            case TradingInfoSymbolInfoFeatureEnum.MinimalLot: {
                const minLot = instrument.getMinLot(productType);
                return MathUtils.formatValueWithEps(minLot);
            }
            case TradingInfoSymbolInfoFeatureEnum.MaximumLot: {
                const maxLot = instrument.getMaxLot(productType);
                if (maxLot > 0 && maxLot !== NumericUtils.MAXVALUE) {
                    return MathUtils.formatValueWithEps(maxLot);
                }
                else {
                    return Resources.getResource('InstrumentDetailsPanel.None');
                }
            }
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionQuantity: {
                const currenRiskPlanItem = instrument.RiskSettings.RiskPlanSettings.cache[productType];
                if ((currenRiskPlanItem === null || currenRiskPlanItem === void 0 ? void 0 : currenRiskPlanItem.MaxPositionQtyPerSymbol) != null &&
                    currenRiskPlanItem.MaxPositionQtyPerSymbol !== -1) {
                    return currenRiskPlanItem.MaxPositionQtyPerSymbol.toString();
                }
                else {
                    return Resources.getResource('InstrumentDetailsPanel.None');
                }
            }
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionValue: {
                const maxPositionValue = instrument.getMaxPositionValue(productType);
                if (isValidNumber(maxPositionValue) && maxPositionValue !== -1) {
                    const asset = (_a = DataCache.GetAssetById(instrument.getPositionValueCurrencyId(productType, account))) !== null && _a !== void 0 ? _a : account.assetBalanceDefault.Asset;
                    return CurrencyUtils.FormatWithCurrencySymbol(asset.formatPrice(maxPositionValue, true), asset.Name);
                }
                else {
                    return Resources.getResource('InstrumentDetailsPanel.None');
                }
            }
            case TradingInfoSymbolInfoFeatureEnum.AllowShortPositions: {
                const isAllowShortPositions = instrument.IsAllowShortPositions(productType);
                return Resources.getResource(isAllowShortPositions
                    ? 'InstrumentDetailsPanel.Allow short positions'
                    : 'InstrumentDetailsPanel.Not allow short positions');
            }
        }
    }
    getTradingInfoLocalizationKeyProductType(key, instrument, productType) {
        const useProductType = RiskPlan.GetAvailableProductTypes(null, instrument).length > 1;
        const marginLocKey = this.getTradingInfoLocalizationKey(key);
        if (!useProductType) {
            return marginLocKey;
        }
        const separator = '.';
        const productTypeSuffix = this.GetProductTypeSuffix(productType);
        return marginLocKey + separator + productTypeSuffix;
    }
    getTradingInfoLocalizationKey(key) {
        switch (key) {
            case TradingInfoSymbolInfoFeatureEnum.MinimalLot:
                return 'InstrumentDetailsPanel.MinimalLot';
            case TradingInfoSymbolInfoFeatureEnum.MaximumLot:
                return 'InstrumentDetailsPanel.MaximumLot';
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionQuantity:
                return 'InstrumentDetailsPanel.MaxPositionQtyPerSymbol';
            case TradingInfoSymbolInfoFeatureEnum.MaxPositionValue:
                return 'InstrumentDetailsPanel.MaxPositionValuePerSymbol';
            case TradingInfoSymbolInfoFeatureEnum.AllowShortPositions:
                return 'InstrumentDetailsPanel.AllowShortPositions';
            default:
                return key.toString();
        }
    }
    addTickSizeCostItems(instrument) {
        let items = [];
        if (instrument.VariableTickList == null || instrument.VariableTickList.length <= 1) {
            if (instrument.QuotingType === QuotingType.TickCost_TickSize) {
                const tickCoastName = Resources.getResource('InstrumentDetailsPanel.TickCoast');
                const tickCoastValue = this.getTickCoast(instrument);
                items.push([tickCoastName, tickCoastValue]);
            }
            const tickSizeName = Resources.getResource('InstrumentDetailsPanel.TickSize');
            const tickSizeValue = MathUtils.formatValueWithEps(instrument.PointSize);
            items.push([tickSizeName, tickSizeValue]);
        }
        else {
            if (instrument.QuotingType !== QuotingType.LotSize) {
                items.push([Resources.getResource('InstrumentDetailsPanel.TickCoast'), '']);
                items = items.concat(this.PrepareTickSizeCostItems(instrument, TickSizeTickCoastEnum.KEY_TickCoast));
            }
            items.push([Resources.getResource('InstrumentDetailsPanel.TickSize'), '']);
            items = items.concat(this.PrepareTickSizeCostItems(instrument, TickSizeTickCoastEnum.KEY_TickSize));
        }
        if (instrument.QuotingType === QuotingType.LotSize) {
            const lotSizeName = Resources.getResource('InstrumentDetailsPanel.LotSize');
            const lotSizeValue = this.getLotSize(instrument);
            items.push([lotSizeName, lotSizeValue]);
        }
        return items;
    }
    getLotSize(instrument) {
        let stLotSize = instrument.LotSize.toString();
        if (instrument.isForexSymbol()) {
            stLotSize += ' ' + instrument.Exp1;
        }
        if (instrument.QuotingType === QuotingType.LotSize) {
            return stLotSize;
        }
        return '';
    }
    getTickCoast(instrument) {
        if (instrument.InstrType === InstrumentTypes.FUTURES ||
            instrument.InstrType === InstrumentTypes.CFD_FUTURES ||
            instrument.InstrType === InstrumentTypes.OPTIONS ||
            instrument.InstrType === InstrumentTypes.SPREADBET) {
            let tickCoast = instrument.FuturesTickCoast;
            if (tickCoast < 0) {
                tickCoast = instrument.LotSize * instrument.PointSize;
            }
            return tickCoast.toString();
        }
        else {
            const tickCoast = instrument.FuturesTickCoast;
            return tickCoast.toString();
        }
    }
    getTickSizeTickCoastFormatString(lotsFrom, lotsTo, allowLimit, allowHighLimit, feeVar) {
        const lf = parseFloat(lotsFrom);
        const lt = parseFloat(lotsTo);
        const localizeKey = Resources.getResource(feeVar === FormatStringEnum.Price ? 'InstrumentDetailsPanel.Price' : 'InstrumentDetailsPanel.Amount');
        return (lf +
            (allowLimit ? ' <= ' : ' < ') +
            localizeKey +
            (allowHighLimit ? ' <= ' : ' < ') +
            (lt === -1 ? '\u221e' /* ∞ */ : lt.toString()));
    }
    getHistoricalSwap(instrument, shortKey) {
        if (!instrument) {
            return null;
        }
        if (!instrument.InstrumentAdditionalInfo) {
            return null;
        }
        if (instrument.InstrumentAdditionalInfo.hasOwnProperty(shortKey)) {
            return instrument.InstrumentAdditionalInfo[shortKey];
        }
        return null;
    }
    getGeneralInfoFeatureKey(feature) {
        switch (feature) {
            case GeneralSymbolInfoFeatureEnum.SymbolName:
                return 'InstrumentDetailsPanel.Symbol';
            case GeneralSymbolInfoFeatureEnum.SymbolDescription:
                return 'InstrumentDetailsPanel.Description';
            case GeneralSymbolInfoFeatureEnum.TradingExchage:
                return 'InstrumentDetailsPanel.ExchangeTrading';
            case GeneralSymbolInfoFeatureEnum.MarketDataExchange:
                return 'InstrumentDetailsPanel.ExchangeMarketData';
            case GeneralSymbolInfoFeatureEnum.SymbolType:
                return 'InstrumentDetailsPanel.AssetClass';
            case GeneralSymbolInfoFeatureEnum.Underlier:
                return 'InstrumentDetailsPanel.Underlier';
            case GeneralSymbolInfoFeatureEnum.DeliveryMethod:
                return 'InstrumentDetailsPanel.DeliveryMethod';
            case GeneralSymbolInfoFeatureEnum.ContactMonth:
                return 'InstrumentDetailsPanel.ContactMonth';
            case GeneralSymbolInfoFeatureEnum.FirstTradeDate:
                return 'InstrumentDetailsPanel.FirstTradeDate';
            case GeneralSymbolInfoFeatureEnum.LastTradeDate:
                return 'InstrumentDetailsPanel.LastTradeDate';
            case GeneralSymbolInfoFeatureEnum.MaturityDate:
                return 'InstrumentDetailsPanel.MaturityDate';
            case GeneralSymbolInfoFeatureEnum.NoticeDate:
                return 'InstrumentDetailsPanel.NoticeDate';
            case GeneralSymbolInfoFeatureEnum.SettlementDate:
                return 'InstrumentDetailsPanel.SettlementDate';
            case GeneralSymbolInfoFeatureEnum.TradingBalance:
                return 'InstrumentDetailsPanel.TradingBalance';
            case GeneralSymbolInfoFeatureEnum.Isin:
                return 'InstrumentDetailsPanel.ISIN';
            case GeneralSymbolInfoFeatureEnum.StrikePrice:
                return 'InstrumentDetailsPanel.StrikePrice';
            case GeneralSymbolInfoFeatureEnum.ExerciseStyle:
                return 'InstrumentDetailsPanel.ExerciseStyle';
            case GeneralSymbolInfoFeatureEnum.FaceValue:
                return 'InstrumentDetailsPanel.FaceValue';
            case GeneralSymbolInfoFeatureEnum.CouponCycle:
                return 'InstrumentDetailsPanel.CouponCycle';
            case GeneralSymbolInfoFeatureEnum.CouponRate:
                return 'InstrumentDetailsPanel.CouponRate';
            case GeneralSymbolInfoFeatureEnum.Ytm:
                return 'InstrumentDetailsPanel.YTM';
            case GeneralSymbolInfoFeatureEnum.AccruedInterest:
                return 'InstrumentDetailsPanel.AccruedInterest';
            case GeneralSymbolInfoFeatureEnum.PrevPaymentDate:
                return 'InstrumentDetailsPanel.PreviousPaymentDate';
            case GeneralSymbolInfoFeatureEnum.NextPaymentDate:
                return 'InstrumentDetailsPanel.NextPaymentDate';
            case GeneralSymbolInfoFeatureEnum.DaysPerMonth:
                return 'InstrumentDetailsPanel.FixedIncomeDaysPerMonth';
            case GeneralSymbolInfoFeatureEnum.DaysPerYear:
                return 'InstrumentDetailsPanel.FixedIncomeDaysPerYear';
            case GeneralSymbolInfoFeatureEnum.Yield:
                return 'InstrumentDetailsPanel.Yield';
            case GeneralSymbolInfoFeatureEnum.Asset:
                return 'InstrumentDetailsPanel.Exp1';
            case GeneralSymbolInfoFeatureEnum.Industry:
                return 'InstrumentDetailsPanel.Industry';
            case GeneralSymbolInfoFeatureEnum.Sector:
                return 'InstrumentDetailsPanel.Sector';
            case GeneralSymbolInfoFeatureEnum.ExtSmFlag:
            default:
                return `General item ${feature} not found in InstrumentFeatureHelper::getGeneralInfoFeatureString`;
        }
    }
}
export const InstrumentFeatureHelper = new _InstrumentFeatureHelper();
var MarginEnum;
(function (MarginEnum) {
    MarginEnum[MarginEnum["KEY_Margin"] = 0] = "KEY_Margin";
    MarginEnum[MarginEnum["KEY_MarginBuy"] = 1] = "KEY_MarginBuy";
    MarginEnum[MarginEnum["KEY_MarginSell"] = 2] = "KEY_MarginSell";
    MarginEnum[MarginEnum["KEY_MarginDay"] = 3] = "KEY_MarginDay";
    MarginEnum[MarginEnum["KEY_MarginDayBuy"] = 4] = "KEY_MarginDayBuy";
    MarginEnum[MarginEnum["KEY_MarginDaySell"] = 5] = "KEY_MarginDaySell";
    MarginEnum[MarginEnum["KEY_MarginOvernight"] = 6] = "KEY_MarginOvernight";
    MarginEnum[MarginEnum["KEY_MarginOvernightBuy"] = 7] = "KEY_MarginOvernightBuy";
    MarginEnum[MarginEnum["KEY_MarginOvernightSell"] = 8] = "KEY_MarginOvernightSell";
    MarginEnum[MarginEnum["KEY_Leverage"] = 9] = "KEY_Leverage";
})(MarginEnum || (MarginEnum = {}));
var TickSizeTickCoastEnum;
(function (TickSizeTickCoastEnum) {
    TickSizeTickCoastEnum[TickSizeTickCoastEnum["KEY_TickSize"] = 0] = "KEY_TickSize";
    TickSizeTickCoastEnum[TickSizeTickCoastEnum["KEY_TickCoast"] = 1] = "KEY_TickCoast";
})(TickSizeTickCoastEnum || (TickSizeTickCoastEnum = {}));
var FormatStringEnum;
(function (FormatStringEnum) {
    FormatStringEnum[FormatStringEnum["Price"] = 0] = "Price";
    FormatStringEnum[FormatStringEnum["Amount"] = 1] = "Amount";
})(FormatStringEnum || (FormatStringEnum = {}));
